export type SkyCoordinates = {
    /** Right ascension in hours, 0 to 24. */
    ra: number
    /** Declination in degrees, -90 to 90. */
    dec: number
}

export type SkyPointerProvider = (clientX: number, clientY: number) => SkyCoordinates | null

const providers = new Map<Element, SkyPointerProvider>()

/**
 * Lets a sky scene report the real coordinates under the pointer.
 * The element should carry a data-sky attribute so the cursor can find it.
 */
export const registerSkyPointer = (element: Element, provider: SkyPointerProvider): (() => void) => {
    providers.set(element, provider)

    return () => {
        if (providers.get(element) === provider) providers.delete(element)
    }
}

export const readSkyPointer = (target: Element | null, clientX: number, clientY: number): SkyCoordinates | null => {
    const host = target?.closest('[data-sky]')

    if (!host) return null

    const provider = providers.get(host)

    return provider ? provider(clientX, clientY) : null
}

export const formatRightAscension = (hours: number): string => {
    const value = ((hours % 24) + 24) % 24
    const h = Math.floor(value)
    const minutesFloat = (value - h) * 60
    const m = Math.floor(minutesFloat)
    const s = Math.floor((minutesFloat - m) * 60)

    return `${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`
}

export const formatDeclination = (degrees: number): string => {
    const sign = degrees < 0 ? '−' : '+'
    const abs = Math.abs(degrees)
    const d = Math.floor(abs)
    const minutesFloat = (abs - d) * 60
    const m = Math.floor(minutesFloat)
    const s = Math.floor((minutesFloat - m) * 60)

    return `${sign}${String(d).padStart(2, '0')}° ${String(m).padStart(2, '0')}′ ${String(s).padStart(2, '0')}″`
}
